/**
 * Timestamp display helpers.
 *
 * The backend stores timestamps as IST wall-clock time in a NAIVE form (no timezone
 * offset) — e.g. "2026-07-22T12:19:10.614000". Those digits are already the value we
 * want to show, so we display them VERBATIM and never apply a timezone conversion.
 * (Converting to Asia/Kolkata would shift the value by +5:30 on any non-IST runtime.)
 *
 * Relative-time ("x minutes ago") still needs a real instant, so it interprets the
 * stored wall-clock as IST when computing the difference from now.
 */

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

interface WallClock {
  y: number;
  mo: number;
  d: number;
  hh: number;
  mi: number;
  ss: number;
}

/** Extract literal Y-M-D H:M:S from an ISO-ish value, ignoring any timezone suffix. */
const parseWallClock = (input?: string | Date | null): WallClock | null => {
  if (!input) return null;
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    return {
      y: input.getFullYear(),
      mo: input.getMonth() + 1,
      d: input.getDate(),
      hh: input.getHours(),
      mi: input.getMinutes(),
      ss: input.getSeconds(),
    };
  }
  const m = String(input).match(/(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  return {
    y: +m[1],
    mo: +m[2],
    d: +m[3],
    hh: m[4] ? +m[4] : 0,
    mi: m[5] ? +m[5] : 0,
    ss: m[6] ? +m[6] : 0,
  };
};

/**
 * Build a Date whose LOCAL wall-clock equals the stored value. Formatting it with
 * Intl/toLocale* (without a `timeZone` option) then prints the stored digits on any
 * runtime, regardless of the browser's timezone. Returns null if unparseable.
 */
export const toDisplayDate = (input?: string | Date | null): Date | null => {
  const p = parseWallClock(input);
  if (!p) return null;
  return new Date(p.y, p.mo - 1, p.d, p.hh, p.mi, p.ss);
};

/** Format a stored timestamp for display, VERBATIM (no timezone conversion). */
export const formatInIst = (
  dateStr?: string | Date | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }
): string => {
  const date = toDisplayDate(dateStr);
  if (!date) return '-';
  // No `timeZone` here on purpose — the stored value is already the value to show.
  return new Intl.DateTimeFormat('en-IN', options).format(date);
};

/* ------------------------------------------------------------------ UTC-stored
 * The helpers above assume a naive timestamp holds IST wall-clock digits. That
 * is true for some collections (personnel_master) but NOT for notifications,
 * which store a true UTC instant — verified against the ObjectId generation
 * time. FastAPI serialises it without a Z or offset, so the string is
 * indistinguishable from the IST-wall-clock form and the helpers above render it
 * 5:30 early: a notification one minute old read "5 hours ago".
 *
 * Use these for any value known to be stored as real UTC.
 */

/** Parse a UTC-stored timestamp into a true instant, with or without a Z/offset. */
export const parseUtcInstant = (input?: string | Date | null): Date | null => {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;

  const s = String(input).trim();
  // Append Z only when no timezone is present, so values that already carry an
  // offset keep it. Without this, JS reads a bare date-time form as LOCAL time.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const d = new Date(hasZone ? s : `${s.replace(' ', 'T')}Z`);
  return isNaN(d.getTime()) ? null : d;
};

/** Format a UTC-stored timestamp in IST. */
export const formatUtcInIst = (
  input?: string | Date | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }
): string => {
  const d = parseUtcInstant(input);
  if (!d) return '-';
  return new Intl.DateTimeFormat('en-IN', { ...options, timeZone: 'Asia/Kolkata' }).format(d);
};

/** "x minutes ago" for a UTC-stored timestamp. */
export const getRelativeTimeFromUtc = (input?: string | Date | null): string => {
  const d = parseUtcInstant(input);
  if (!d) return '-';

  const diffSeconds = Math.round((Date.now() - d.getTime()) / 1000);
  // Small clock skew between server and browser must not read as the future.
  if (diffSeconds < 60) return 'just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatUtcInIst(input);
};

/**
 * Return a short human-friendly "time ago" string (e.g. "2 minutes ago").
 * Falls back to an absolute (verbatim) timestamp for anything older than ~30 days.
 */
export const getRelativeTimeInIst = (dateStr?: string | Date | null): string => {
  const p = parseWallClock(dateStr);
  if (!p) return '-';

  // The stored wall-clock is IST; convert to a true epoch to diff against now.
  const epoch = Date.UTC(p.y, p.mo - 1, p.d, p.hh, p.mi, p.ss) - IST_OFFSET_MS;
  const diffSeconds = Math.round((Date.now() - epoch) / 1000);

  if (diffSeconds < 60) return 'just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatInIst(dateStr);
};
